import React, { useEffect, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { Trimester } from "../../utility/interfaces/carePlanInterfaces";
import CarePlanHeader from "../care-plan-widget/care-plan-widget-content/CarePlanHeader";
import CarePlanProgress from "../care-plan-widget/care-plan-widget-content/CarePlanProgress";
import CarePlanActivityList from "../care-plan-widget/care-plan-widget-content/CarePlanActivityList";
import HeartFilledIcon from "../../components/icon-components/HeartFilledIcon";
import CustomStatusPill from "../../components/custom-status-pill/CustomStatusPill";
import { sortActivitiesByType } from "../../utility/functions/common-functions";

import styles from "./CarePlanAccordion.module.scss";
import { RootState } from "../../store/store";
import {
  selectExpandedGroupIds,
  setExpandedGroups,
  toggleGroup,
} from "../../pages/V2Pages/my-care-plan-page/services/carePlanUISlice";
import { GREY } from "../../utility/constants/common-constants";

interface Props {
  data: Trimester[];
}

const CarePlanAccordion: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:900px)");
  const dispatch = useDispatch();

  const didInitialize = useRef(false);

  const expandedGroupIds = useSelector((state: RootState) =>
    selectExpandedGroupIds(state)
  );

  const currentPlan = data
    .flatMap((trimester) => trimester.trimesterPlans)
    .find((plan) => plan.weekType === "Current");

  useEffect(() => {
    if (!didInitialize.current && currentPlan?.displayGroupId != null) {
      if (expandedGroupIds.length === 0) {
        dispatch(setExpandedGroups([currentPlan.displayGroupId]));
      }
      didInitialize.current = true;
    }
  }, [currentPlan, expandedGroupIds, dispatch]);

  useEffect(() => {
    const targetId =
      expandedGroupIds[expandedGroupIds.length - 1] ??
      currentPlan?.displayGroupId;
    if (targetId != null) {
      const el = document.getElementById(`accordion-${targetId}`);
      setTimeout(
        () => el?.scrollIntoView({ behavior: "smooth", block: "start" }),
        1000
      );
    }
  }, []);

  const handleToggle = (id: number) => {
    dispatch(toggleGroup(id));
  };

  return (
    <>
      {data.map((trimester, ti) => (
        <Box key={ti} className={styles.wrapper}>
          {trimester.trimesterDisplayName && (
            <Box className={styles.trimesterTitle}>
              <HeartFilledIcon style={{ marginRight: 5 }} />
              <Typography
                variant="bodySmall"
                fontWeight={700}
                letterSpacing="0.72px"
                color="common.black"
              >
                {trimester.trimesterDisplayName.toUpperCase()}
              </Typography>
            </Box>
          )}

          {trimester.trimesterPlans.map((plan) => {
            const isExpanded = expandedGroupIds.includes(plan.displayGroupId);
            const sortedActivities = sortActivitiesByType(plan.activities);

            const totalPoints = plan.activities.reduce(
              (sum, a) => sum + a.activityPoints,
              0
            );
            const earnedPoints = plan.activities
              .filter((a) => a.activityCompletionStatus === t("completed"))
              .reduce((sum, a) => sum + a.activityPoints, 0);
            const completedCount = plan.activities.filter(
              (a) => a.activityCompletionStatus === t("completed")
            ).length;

            return (
              <Accordion
                key={plan.displayGroupId}
                id={`accordion-${plan.displayGroupId}`}
                expanded={isExpanded}
                onChange={() => handleToggle(plan.displayGroupId)}
                sx={{
                  scrollMarginTop: "5.5rem",
                  borderRadius: "12px",
                  "&:last-of-type": {
                    borderRadius: "12px",
                    marginBottom: "3.5rem",
                  },
                  boxShadow: "none",
                  border: `1px solid ${GREY}`,
                  mb: "1.5rem",
                  "&::before": { display: "none" },
                  "&.Mui-expanded": {
                    mb: "1.5rem",
                    "&:last-of-type": {
                      marginBottom: "3.5rem",
                    },
                  },
                  "@media (max-width:768px)": { my: "1.5rem" },
                }}
              >
                <AccordionSummary
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    p: { xs: "1.25rem", md: "1.5rem 2.5rem" },
                    "& .MuiAccordionSummary-content": {
                      m: 0,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    },
                  }}
                >
                  <Box className={styles.accordionSummaryContent}>
                    <Box className={styles.accordionHeader}>
                      <Box>
                        <Typography variant="h3">
                          {plan.displayGroupDisplayName}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <CustomStatusPill
                          statusCode={plan.groupProgressStatusCode}
                          status={plan.groupProgressStatus}
                        />
                        <ExpandMoreIcon
                          sx={{
                            transform: isExpanded
                              ? "rotate(180deg)"
                              : undefined,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(plan.displayGroupId);
                          }}
                        />
                      </Box>
                    </Box>
                    {!isExpanded && (
                      <CarePlanProgress
                        totalPoints={totalPoints}
                        earnedPoints={earnedPoints}
                        completedCount={completedCount}
                        totalActivities={plan.activities.length}
                        hideBorder
                      />
                    )}
                  </Box>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    p: isMobile ? "1rem" : "1.5rem 2.5rem",
                  }}
                >
                  <CarePlanHeader description={plan.headerTextUnderGroup} />
                  <CarePlanProgress
                    totalPoints={totalPoints}
                    earnedPoints={earnedPoints}
                    completedCount={completedCount}
                    totalActivities={plan.activities.length}
                  />
                  <CarePlanActivityList activities={sortedActivities} />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ))}
    </>
  );
};

export default CarePlanAccordion;
